import { useContext } from "react";
import { Box, Button, Typography } from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { useNavigate, useParams } from "react-router-dom";
import { Layer } from "../../types/layer";
import { DrawingContext, DrawingMode, DrawingFeatureType } from "../../contexts/DrawingContext";
import LayerControlItem from "./LayerControlItem";
import ForestEditIcon from "../icons/ForestEditIcon";
import { useAuth } from "../../contexts/AuthContext";
import { isViewOnly } from "../../utils/permissions";
import { useDataReviewSummary } from "../../api/dataReview";
import { fmtArea, geometryAreaM2 } from "../vegEditor/editorModel";

interface VegetationControlProps {
  layer: Layer | null;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  onPanelOpenChange?: (open: boolean) => void;
}

const VegetationControl: React.FC<VegetationControlProps> = ({ layer, expanded, onExpandedChange, onPanelOpenChange }) => {
  const { drawingMode, setDrawingMode, drawingFeatureType, setDrawingFeatureType } = useContext(DrawingContext);

  const { user } = useAuth();
  const viewOnly = isViewOnly(user);
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  // A refinement under way (parsed or parsing, not applied) can be picked up.
  const { data: summaryRes } = useDataReviewSummary(viewOnly ? undefined : projectId);
  const inProgress = (summaryRes?.data?.jobs ?? []).find((j) => j.status === "queued" || j.status === "running" || j.status === "review") ?? null;
  if (!layer) return null;

  const handleToggleVisibility = () => {
    layer.toolbarButton?.props?.onClick?.();
  };

  const handleDrawClick = (featureType: string) => {
    if (drawingMode === DrawingMode.VEGETATION_EDIT) {
      setDrawingMode(null);
      setDrawingFeatureType(null);
    } else {
      setDrawingMode(DrawingMode.VEGETATION_EDIT);
      setDrawingFeatureType(featureType);
    }
  };

  const drawingContent = viewOnly ? null : (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {inProgress && (
        <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", p: "10px 12px", borderRadius: 1.5, bgcolor: "rgba(255,255,255,0.06)" }}>
          <Box sx={{ flex: "0 0 8px", width: 8, height: 8, borderRadius: "50%", bgcolor: "#ff7a45", mt: "5px", boxShadow: "0 0 0 3px rgba(255,122,69,0.22)" }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{inProgress.name} in progress</Typography>
            <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.6)", mt: 0.25 }}>
              {inProgress.area ? fmtArea(geometryAreaM2(inProgress.area)) : "Whole project"}{inProgress.options?.start === "clean" ? " · clean slate" : ""}
            </Typography>
          </Box>
        </Box>
      )}
      <Button
        size="small"
        variant="contained"
        disabled={!layer.rawSource}
        startIcon={<AutoFixHighIcon sx={{ fontSize: 16 }} />}
        onClick={() => navigate(`/projects/${projectId}/vegetation${inProgress ? "?resume=1" : ""}`)}
        title={layer.rawSource ? "Find features in imagery, paint and wand-edit the layer" : "The vegetation layer is still loading"}
        sx={{ height: 38, fontWeight: 600, bgcolor: "#00d4ad", color: "#06302a", "&:hover": { bgcolor: "#19e0bb" },
          "&.Mui-disabled": { bgcolor: "rgba(0,212,173,0.3)", color: "rgba(255,255,255,0.5)" } }}
      >
        {inProgress ? "Resume refinement" : "Edit vegetation"}
      </Button>
      <Typography sx={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.9, textTransform: "uppercase", color: "rgba(255,255,255,0.45)", mt: 1 }}>Other tools</Typography>
      <Button
        size="small"
        variant={drawingMode === DrawingMode.VEGETATION_EDIT ? "contained" : "outlined"}
        onClick={() => handleDrawClick(DrawingFeatureType.POLYGON)}
        sx={{
          color: drawingMode === DrawingMode.VEGETATION_EDIT ? "#fff" : "#00d4ad",
          borderColor: "#00d4ad",
          backgroundColor: drawingMode === DrawingMode.VEGETATION_EDIT ? "#00d4ad" : "transparent",
          "&:hover": {
            backgroundColor: drawingMode === DrawingMode.VEGETATION_EDIT ? "#00b899" : "rgba(0, 212, 173, 0.1)",
          },
        }}
      >
        Edit Polygon
      </Button>
      <Button
        size="small"
        variant={drawingMode === DrawingMode.VEGETATION_EDIT ? "contained" : "outlined"}
        onClick={() => handleDrawClick(DrawingFeatureType.LINE)}
        sx={{
          color: drawingMode === DrawingMode.VEGETATION_EDIT ? "#fff" : "#00d4ad",
          borderColor: "#00d4ad",
          backgroundColor: drawingMode === DrawingMode.VEGETATION_EDIT ? "#00d4ad" : "transparent",
          "&:hover": {
            backgroundColor: drawingMode === DrawingMode.VEGETATION_EDIT ? "#00b899" : "rgba(0, 212, 173, 0.1)",
          },
        }}
      >
        Edit Line
      </Button>
      <Button
        size="small"
        variant={drawingMode === DrawingMode.VEGETATION_EDIT && drawingFeatureType === DrawingFeatureType.UPLOAD ? "contained" : "outlined"}
        onClick={() => handleDrawClick(DrawingFeatureType.UPLOAD)}
        sx={{
          color: drawingMode === DrawingMode.VEGETATION_EDIT && drawingFeatureType === DrawingFeatureType.UPLOAD ? "#fff" : "#00d4ad",
          borderColor: "#00d4ad",
          backgroundColor: drawingMode === DrawingMode.VEGETATION_EDIT && drawingFeatureType === DrawingFeatureType.UPLOAD ? "#00d4ad" : "transparent",
          "&:hover": {
            backgroundColor: drawingMode === DrawingMode.VEGETATION_EDIT && drawingFeatureType === DrawingFeatureType.UPLOAD ? "#00b899" : "rgba(0, 212, 173, 0.1)",
          },
        }}
      >
        Upload from File
      </Button>
    </Box>
  );

  return (
    <LayerControlItem
      title="Vegetation"
      icon={<ForestEditIcon />}
      visible={layer.active ?? false}
      onToggleVisibility={handleToggleVisibility}
      enabled={!!layer.rawSource}
      drawingContent={drawingContent}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      onPanelOpenChange={onPanelOpenChange}
    />
  );
};

export default VegetationControl;
